import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import {
  getInstructionDiscriminator,
  getMayhemProgram,
} from '../program/mayhemProgram';
import { convertSolToLamports } from '../utils/tradeUnits';

function deriveAssociatedTokenAddress(owner, mint, associatedTokenProgramId, tokenProgramId) {
  const [publicKey] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgramId.toBuffer(), mint.toBuffer()],
    associatedTokenProgramId
  );

  return publicKey;
}

function encodeU64(value) {
  const buffer = new Uint8Array(8);
  let remainingValue = value;

  for (let index = 0; index < 8; index += 1) {
    buffer[index] = Number(remainingValue & 255n);
    remainingValue >>= 8n;
  }

  return buffer;
}

function buildInstructionKeys(instructionDefinition, accounts) {
  return instructionDefinition.accounts.map((account) => {
    const pubkey = accounts[account.name];

    if (!pubkey) {
      throw new Error(`Missing required buy account: ${account.name}`);
    }

    return {
      pubkey,
      isSigner: account.isSigner,
      isWritable: account.isMut,
    };
  });
}

export async function buyCurrentGame({
  solAmount,
  globalConfig,
  globalConfigPubkey,
  currentGameState,
  currentGamePubkey,
  dumpVaultPubkey,
}) {
  const program = getMayhemProgram();
  const provider = program.getPhantomProvider();
  const walletPublicKey = provider?.publicKey || null;

  if (!provider || !walletPublicKey) {
    throw new Error('Connect a Phantom wallet before buying.');
  }

  if (!globalConfig || !globalConfigPubkey) {
    throw new Error('Global config is not ready.');
  }

  if (!currentGameState || !currentGamePubkey) {
    throw new Error('Current game is not ready.');
  }

  if (!dumpVaultPubkey) {
    throw new Error('Global dump vault is not ready.');
  }

  if (!currentGameState.reserve_vault || !currentGameState.mint) {
    throw new Error('Current game accounts are incomplete.');
  }

  const BigIntValue = window.BigInt;

  const lamportsIn = convertSolToLamports(solAmount);

  if (globalConfig.max_buy_lamports && lamportsIn > BigIntValue(globalConfig.max_buy_lamports)) {
    throw new Error('Buy amount exceeds the configured max buy size.');
  }

  const instructionDefinition = program.getInstructionDefinition('buy');

  if (!instructionDefinition) {
    throw new Error('Buy instruction was not found in the local IDL.');
  }

  const roundMint = new PublicKey(currentGameState.mint);
  const buyerTokenAccount = deriveAssociatedTokenAddress(
    walletPublicKey,
    roundMint,
    program.associatedTokenProgramId,
    program.tokenProgramId
  );

  const accounts = {
    globalConfig: new PublicKey(globalConfigPubkey),
    gameState: new PublicKey(currentGamePubkey),
    gameReserveVault: new PublicKey(currentGameState.reserve_vault),
    globalDumpVault: new PublicKey(dumpVaultPubkey),
    platformTreasury: program.getPlatformTreasuryPda(),
    roundMint,
    buyerTokenAccount,
    buyer: walletPublicKey,
    tokenProgram: program.tokenProgramId,
    associatedTokenProgram: program.associatedTokenProgramId,
    systemProgram: program.systemProgramId,
  };

  const discriminator = await getInstructionDiscriminator('buy');
  const data = new Uint8Array([...discriminator, ...encodeU64(lamportsIn)]);
  const instruction = new TransactionInstruction({
    programId: program.programId,
    keys: buildInstructionKeys(instructionDefinition, accounts),
    data,
  });

  const { blockhash, lastValidBlockHeight } = await program.connection.getLatestBlockhash('confirmed');
  const transaction = new Transaction({
    feePayer: walletPublicKey,
    recentBlockhash: blockhash,
  }).add(instruction);

  const response = await provider.signAndSendTransaction(transaction);
  const signature = typeof response === 'string' ? response : response.signature;

  await program.connection.confirmTransaction(
    {
      signature,
      blockhash,
      lastValidBlockHeight,
    },
    'confirmed'
  );

  return signature;
}
